import {
  CloseRounded,
  FileUploadOutlined,
  CloudDownloadOutlined,
} from "@mui/icons-material";
import {
  Box,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  Typography,
  useTheme,
  Chip,
} from "@mui/material";
import { grey, blue } from "@mui/material/colors";
import { isEmpty } from "lodash";
import { useMemo, useRef, useState } from "react";
import { Field, useForm, useFormState } from "react-final-form";
import { v4 as uuidv4 } from "uuid";
import { VALIDATOR_REQUIRE } from "../../../utils/finalForm/validators/VALIDATOR_REQUIRE";
import Txt from "../../typography/Txt";
import RffInputWrapper from "../shared/RffInputWrapper";

const RffFileUploadField = ({
  /** (Optional) If provided, we tag each uploaded file with this docGroup. */
  docGroup,

  /** (Optional) If provided, we tag each uploaded file with this docType. */
  docType,

  /** (Optional) Array of docGroups to exclude from display */
  excludeDocGroups = [],

  /** (Optional) Array of docTypes to exclude from display */
  excludeDocTypes = [],

  /** Label for the UI */
  label,

  /** If true, runs a required validator on the "files" array. */
  required,

  /** If using within a grid system or specifying its width, etc. */
  suppressGrid,
  size,
  suppressIcon,
  /** Visible height of the dropzone */
  height = "180px",
  uploadText = "Drag & drop files here or click to select files",
  /** Additional styles for the outer dropzone box */
  sx,
  suppressButton,
  helperText,
  /** Border size for drag states */
  borderSize = 3,
  /** Background color */
  disabled = false,
  background = "transparent",
  hideFileList = false, // If true, hides the list of uploaded files
}) => {
  const theme = useTheme();
  const dragCounter = useRef(0);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const form = useForm();

  // Subscribe to borrowerDocuments changes
  const { values } = useFormState({
    subscription: { values: true },
  });

  // If "required" is true, we'll run the standard required validator
  const validate = useMemo(
    () => (required ? VALIDATOR_REQUIRE : undefined),
    [required],
  );

  // Drag state management
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDraggingOver(false);
    }
  };

  /**
   * Helper function to get the ID from a document object
   * Checks for _id first, then falls back to oid
   */
  const getDocId = (doc) => {
    return doc._id || doc.oid;
  };

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      required={required}
      helperText={helperText}
      sx={sx}
    >
      <Field
        name="uploadMetadata"
        validate={validate}
        subscription={{ value: true, error: true, touched: true }}
        render={({ input, meta }) => {
          // Get uploaded files from uploadMetadata
          const uploadedFiles = Array.isArray(input.value) ? input.value : [];

          // Get imported documents from borrowerDocuments using the subscribed values
          const importedDocs = Array.isArray(values.borrowerDocuments)
            ? values.borrowerDocuments
            : [];

          /**
           * Filter uploaded files by docGroup/docType and exclusions
           */
          const filteredUploadedFiles = uploadedFiles.filter((fileObj) => {
            // Check exclusions first
            if (excludeDocGroups.includes(fileObj.docGroup)) return false;
            if (excludeDocTypes.includes(fileObj.docType)) return false;

            // If no docGroup/docType specified, show all (except excluded)
            if (!docGroup && !docType) return true;

            const matchesGroup = docGroup
              ? fileObj.docGroup === docGroup
              : !fileObj.docGroup;

            const matchesType = docType
              ? fileObj.docType === docType
              : !fileObj.docType;

            return matchesGroup && matchesType;
          });

          /**
           * Filter imported documents by docGroup/docType and exclusions
           */
          const filteredImportedDocs = importedDocs.filter((doc) => {
            // Check exclusions first
            if (excludeDocGroups.includes(doc.docGroup)) return false;
            if (excludeDocTypes.includes(doc.docType)) return false;

            // If no docGroup/docType specified, show all (except excluded)
            if (!docGroup && !docType) return true;

            const matchesGroup = docGroup
              ? doc.docGroup === docGroup
              : !doc.docGroup;

            const matchesType = docType
              ? doc.docType === docType
              : !doc.docType;

            return matchesGroup && matchesType;
          });

          // Combine both lists for display
          const allDisplayedFiles = [
            ...filteredImportedDocs.map((doc) => ({
              ...doc,
              isImported: true,
            })),
            ...filteredUploadedFiles.map((file) => ({
              ...file,
              isImported: false,
            })),
          ];

          /**
           * Handle adding new files to uploadMetadata
           */
          const onFilesAdded = (newRawFiles) => {
            const newFiles = newRawFiles.map((rawFile) => {
              const newId = uuidv4();

              return {
                id: newId,
                file: rawFile,
                name: rawFile.name,
                file_display_name: rawFile.name,
                docGroup: docGroup || null,
                docType: docType || null,
              };
            });

            const updatedFiles = [...uploadedFiles, ...newFiles];
            input.onChange(updatedFiles);
          };

          /**
           * Delete a file (either uploaded or imported)
           */
          const onFileDelete = (fileToDelete) => {
            if (fileToDelete.isImported) {
              // Remove from borrowerDocuments using _id or oid
              const fileId = getDocId(fileToDelete);
              const updatedImportedDocs = importedDocs.filter(
                (doc) => getDocId(doc) !== fileId,
              );
              form.change("borrowerDocuments", updatedImportedDocs);
            } else {
              // Remove from uploadMetadata
              const updatedFiles = uploadedFiles.filter(
                (f) => f.id !== fileToDelete.id,
              );
              input.onChange(updatedFiles);
            }
          };

          /**
           * Drag & drop events
           */
          const handleDragOver = (e) => {
            e.preventDefault();
            e.stopPropagation();
          };

          const handleDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current = 0;
            setIsDraggingOver(false);
            if (!e.dataTransfer.files) return;
            const droppedFiles = Array.from(e.dataTransfer.files);
            onFilesAdded(droppedFiles);
          };

          /**
           * Standard file selection via input[type=file]
           */
          const handleFileChange = (e) => {
            if (!e.target.files) return;
            onFilesAdded(Array.from(e.target.files));
          };

          /**
           * Programmatically open the file dialog
           */
          const handleClickDropzone = () => {
            const fileInput = document.getElementById(
              `files-fileinput-${docGroup || "noGroup"}-${docType || "noType"}`,
            );
            if (fileInput) fileInput.click();
          };

          return (
            <>
              {/* The Drag & Drop Zone */}
              <Box
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClickDropzone}
                className="rff-file-upload-box"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 100ms ease-in-out",
                  height,
                  padding: "1rem",
                  gap: 2,
                  borderRadius: "4px",
                  background: isDraggingOver
                    ? `${theme.palette.primary.light}20`
                    : background,
                  ...(sx || {}),
                  border: isDraggingOver
                    ? `${borderSize}px dashed ${theme.palette.primary.main}`
                    : `${borderSize}px dashed ${grey[300]}`,
                  color: isDraggingOver
                    ? theme.palette.primary.main
                    : grey[600],
                  borderBottomLeftRadius: isEmpty(allDisplayedFiles)
                    ? "4px"
                    : "0",
                  borderBottomRightRadius: isEmpty(allDisplayedFiles)
                    ? "4px"
                    : "0",
                }}
              >
                {suppressIcon ? null : (
                  <FileUploadOutlined
                    sx={{ opacity: isDraggingOver ? 0.8 : 0.55 }}
                  />
                )}
                <Txt
                  className="file-upload-text"
                  sx={{
                    fontSize: "13px",
                    opacity: isDraggingOver ? 0.9 : 0.7,
                  }}
                >
                  {uploadText}
                </Txt>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id={`files-fileinput-${docGroup || "noGroup"}-${docType || "noType"}`}
                />
                {suppressButton ? null : (
                  <Typography
                    sx={{
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 700,
                      padding: "4px 10px",
                      border: `1px solid ${theme.palette.primary.main}`,
                      borderRadius: "4px",
                      background: isDraggingOver
                        ? `${theme.palette.primary.light}30`
                        : "transparent",
                    }}
                  >
                    Browse Files
                  </Typography>
                )}
              </Box>

              {/* Show validation error if needed */}
              {meta.touched && meta.error && (
                <FormHelperText error>{meta.error}</FormHelperText>
              )}

              {/* Display combined list of imported and uploaded files */}
              {!hideFileList && allDisplayedFiles.length > 0 && (
                <List
                  className="rff-file-upload-list"
                  sx={{
                    listStyle: "none",
                    p: 0.8,
                    pt: 0.3,
                    mb: 2,
                    border: `1px solid ${grey[300]}`,
                    borderBottomLeftRadius: "4px",
                    borderTop: 0,
                    borderBottomRightRadius: "4px",
                  }}
                >
                  {allDisplayedFiles.map((fileObj) => {
                    const fileKey = fileObj.isImported
                      ? getDocId(fileObj)
                      : fileObj.id;

                    return (
                      <ListItem
                        key={fileKey}
                        sx={{
                          background: fileObj.isImported ? blue[50] : grey[100],
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "4px 8px",
                          pr: 0.4,
                          fontSize: "11px !important",
                          display: "flex",
                          borderRadius: "4px",
                          mt: 0.5,
                          border: fileObj.isImported
                            ? `1px solid ${blue[200]}`
                            : `1px solid ${grey[300]}`,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flex: 1,
                          }}
                        >
                          {fileObj.isImported && (
                            <CloudDownloadOutlined
                              sx={{
                                fontSize: "14px",
                                color: blue[600],
                                opacity: 0.7,
                              }}
                            />
                          )}
                          <Txt
                            sx={{
                              fontSize: "11px !important",
                              flex: 1,
                            }}
                          >
                            {fileObj.file_display_name}
                          </Txt>
                          {fileObj.isImported && (
                            <Chip
                              label="Imported"
                              size="small"
                              sx={{
                                height: "18px",
                                fontSize: "10px",
                                backgroundColor: blue[100],
                                color: blue[700],
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                        <IconButton
                          color="error"
                          size="small"
                          sx={{ padding: "4px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onFileDelete(fileObj);
                          }}
                        >
                          <CloseRounded sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </>
          );
        }}
      />
    </RffInputWrapper>
  );
};

export default RffFileUploadField;
